import { useCallback, useMemo, useState } from 'react';
import { isoInstantOf } from '../data/docShape';
import {
  historyRows, historyRowsInPeriod, historySortKeys, historyTypes, historyView,
} from '../domain/notifications';

// The student profile's Message History tab: every message about this student
// or their family, newest first, with what became of it — the one place a
// teacher can see that a message did *not* go (over the day's sending limit,
// refused for a lapsed subscription, bounced by the receiving server or
// undelivered by the carrier), and ask for it again.
//
// The selection, the order, the words, the period (#522) and whether a row
// offers Send again — and to whom (#523) — are `message_history_in` in
// core/src/messages.rs; the arguments are written there. What this file does
// is read a stored message, and a delivery-log row, into the core's record:
// the addresses, the ids off `context`, and the instants as epoch
// milliseconds.

const addresses = (list) => (Array.isArray(list) ? list.map((r) => r?.address).filter(Boolean).map(String) : []);

// A log row's `to` and `cc` are the addresses themselves, not recipient maps
// (functions/delivery/deliver.js `deliveryRow`).
const plainAddresses = (list) => (Array.isArray(list) ? list.filter((a) => a != null && a !== '').map(String) : []);

const str = (v) => (v == null || v === '' ? null : String(v));

const strings = (v) => (Array.isArray(v) ? v.filter((a) => typeof a === 'string') : []);

const millisOf = (value) => {
  const iso = isoInstantOf(value);
  return iso ? Date.parse(iso) : null;
};

// `sendAt` is stored as epoch milliseconds (#599, core/src/messages.rs).
const instantOf = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const asksOf = (v) => (Number.isInteger(v) && v > 0 ? v : 0);

// Who a message went out as (#546): `{ address, name }` as the sender
// recorded it (functions/delivery/deliver.js `deliveryRow`), or nothing on a
// message sent before it did.
const fromOf = (v) => (v && typeof v === 'object'
  ? { fromAddress: str(v.address), fromName: str(v.name) }
  : { fromAddress: null, fromName: null });

/** One stored message as `MessageHistoryInput`. */
export function historyInput(m) {
  const c = m?.context || {};
  return {
    id: String(m?.id ?? ''),
    kind: str(m?.kind),
    channel: str(m?.channel),
    status: str(m?.status),
    to: addresses(m?.to),
    cc: addresses(m?.cc),
    familyId: str(c.familyId),
    studentId: str(c.studentId),
    // A list, even empty, says the message is about named students and not
    // the family; anything else stored there is read as absent.
    studentIds: Array.isArray(c.studentIds) ? c.studentIds.map(String) : null,
    error: str(m?.error),
    sentAt: millisOf(m?.sentAt),
    finishedAt: millisOf(m?.delivery?.finishedAt),
    requestedAt: millisOf(m?.requestedAt),
    asks: asksOf(m?.delivery?.asks),
    // Who the provider has reported delivered, across every round (#523).
    delivered: strings(m?.delivery?.delivered),
    // A teacher's own subject line (#522); the core reads it on that kind only.
    subject: str(m?.subjectLine),
    providerOutcome: null,
    logged: false,
    // MMS's From column and the words the teacher wrote (#546).
    ...fromOf(m?.delivery?.from),
    body: str(m?.note),
    // The outbox keeps no rendered body (#595); the core lends it one from
    // the message's own log rows.
    text: null,
    // A message sent later (#599): when it goes.
    sendAt: instantOf(m?.sendAt),
    // The files it carries (#605), each `{cap}/{name}`; the row lists them
    // by name.
    attachments: strings(c.attachments),
    // When the sender tries a failed message again by itself (#745), epoch
    // milliseconds; the row reads "Will retry" while there is one.
    retryAt: instantOf(m?.delivery?.retryAt),
  };
}

/**
 * One delivery-log row (`messageDeliveries`) as `MessageHistoryInput` (#522):
 * keyed by its message, dated when its attempt ended, failed when the
 * provider said so, and saying why in the receipt's own sentence where there
 * is one.
 */
export function logInput(row) {
  return {
    // A row with no `messageId` (none is written that way) is its own message,
    // under its own id, rather than folded with every other such row.
    id: String(row?.messageId || row?.id || ''),
    kind: str(row?.kind),
    channel: str(row?.channel),
    status: str(row?.status),
    to: plainAddresses(row?.to),
    cc: plainAddresses(row?.cc),
    familyId: str(row?.familyId),
    studentId: str(row?.studentId),
    studentIds: Array.isArray(row?.studentIds) ? row.studentIds.map(String) : null,
    error: str(row?.providerError) ?? str(row?.error),
    sentAt: null,
    finishedAt: millisOf(row?.finishedAt) ?? millisOf(row?.startedAt),
    requestedAt: null,
    asks: asksOf(row?.asks),
    delivered: [],
    subject: str(row?.subject),
    providerOutcome: str(row?.providerOutcome),
    logged: true,
    ...fromOf(row?.from),
    body: str(row?.note),
    // The plain text the sender rendered and kept (#595), which Forward
    // fills the composer with.
    text: str(row?.text),
    sendAt: null,
    // The files the message carried (#605), as the sender recorded them.
    attachments: strings(row?.attachments),
    // A row is one attempt; whether the message will be tried again is the
    // outbox's to say (#745).
    retryAt: null,
  };
}

/**
 * The rows, as plain data. Pure, and exported so a test can assert the
 * selection and the order without rendering. `at` is an ISO string, or null
 * for a message just asked for. With no `period`, every message the outbox
 * holds; with one, the core's period over the outbox and the log.
 */
export function messageHistoryRows(messages, {
  studentId, familyId, period = null, logged = [], now = Date.now(),
} = {}) {
  const inputs = (messages || []).map(historyInput);
  if (!period) return historyRows(inputs, { studentId, familyId });
  return historyRowsInPeriod(inputs, (logged || []).map(logInput), {
    studentId, familyId, period, now,
  });
}

export default function useMessageHistory({
  messages, logged = [], student, period = null, now = null,
}) {
  const studentId = student?.id || null;
  const familyId = student?.familyId || null;
  return useMemo(
    () => messageHistoryRows(messages, {
      studentId, familyId, period, logged, now: now ?? Date.now(),
    }),
    [messages, logged, studentId, familyId, period, now],
  );
}

// ── Sort and Search (#546) ─────────────────────────────────────────────────
//
// MMS's Sort ▾ and Search ▾ over the tab's rows. Which rows a search keeps
// and their order are the core's (`history_search`, `history_sorted`,
// through `historyView`); what is here is what the teacher has chosen.
// Search is a draft until its Search button, as MMS's panel is — typing in
// the box changes nothing until then — and Clear puts both back.

const NO_SEARCH = Object.freeze({ query: '', byType: 'all', withinBody: false });

export function useHistoryView(rows) {
  const sortKeys = useMemo(() => historySortKeys(), []);
  const types = useMemo(() => historyTypes(), []);
  const [sort, setSort] = useState({ key: 'date', descending: true });
  const [search, setSearch] = useState(NO_SEARCH);
  const [draft, setDraft] = useState(NO_SEARCH);

  // Choosing the field already in force turns it round; another starts its
  // own way (Date & Time newest first, the rest A to Z).
  const sortBy = useCallback((key) => {
    setSort((cur) => (cur.key === key
      ? { key, descending: !cur.descending }
      : { key, descending: Boolean(sortKeys.find((k) => k.key === key)?.descending) }));
  }, [sortKeys]);
  const clearSort = useCallback(() => setSort({ key: 'date', descending: true }), []);
  const applySearch = useCallback(() => setSearch(draft), [draft]);
  const clearSearch = useCallback(() => {
    setDraft(NO_SEARCH);
    setSearch(NO_SEARCH);
  }, []);

  const shown = useMemo(
    () => historyView(rows || [], { ...search, sortKey: sort.key, descending: sort.descending }),
    [rows, search, sort],
  );
  return {
    rows: shown,
    sort,
    sortKeys,
    sortBy,
    clearSort,
    types,
    draft,
    setDraft,
    applySearch,
    clearSearch,
    searching: search.query.trim() !== '' || search.byType !== 'all',
  };
}
